import * as React from "react";
import { useRouteError } from "react-router";

import { StaleBuildBanner } from "@/components/ui/stale-build-banner";

/**
 * ChunkErrorElement — errorElement for the root route.
 *
 * When a new build is deployed, Vite renames JS chunks (content hash
 * changes). Any user who has the old app open and navigates to a lazy-
 * loaded page gets:
 *   "Failed to fetch dynamically imported module: .../SettingsMyProfilePage-OldHash.js"
 *
 * MV2-843 (ds/Guards): reload once, silently. If it fails twice inside a
 * minute the stale-build banner appears with a manual reload — never a blank
 * page. The window means a second deploy later in a long-lived tab gets its
 * own silent reload rather than the banner.
 *
 * For all other errors we show a minimal "Something went wrong" UI with a
 * reload button rather than React Router's default developer page.
 */
export const CHUNK_RELOAD_KEY = "mv2:chunk-reload-attempted";
const RETRY_WINDOW_MS = 60_000;

function recentlyAttempted(): boolean {
  try {
    const at = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    return at > 0 && Date.now() - at < RETRY_WINDOW_MS;
  } catch {
    return true;
  }
}

export function ChunkErrorElement(): React.ReactElement | null {
  const error = useRouteError();

  const isChunkError =
    error instanceof TypeError &&
    error.message.includes("Failed to fetch dynamically imported module");

  const [attempted] = React.useState(() => isChunkError && recentlyAttempted());

  React.useEffect(() => {
    if (!isChunkError || attempted) return;
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      /* private mode — fall through to the reload anyway */
    }
    window.location.reload();
  }, [isChunkError, attempted]);

  if (isChunkError) {
    // First failure: the reload is already firing. Second inside a minute:
    // say so, with the one button.
    if (!attempted) return null;
    return (
      <div className="flex min-h-screen items-start justify-center px-4 pt-16">
        <StaleBuildBanner />
      </div>
    );
  }

  // Generic app error — show a friendly message + manual reload.
  const message =
    error instanceof Error ? error.message : "An unexpected error occurred.";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-lg font-semibold text-gray-900">
        Something went wrong
      </h1>
      <p className="max-w-sm text-sm text-gray-500">{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-[color:var(--action)] px-4 py-2 text-sm font-medium text-[color:var(--action-fg)] hover:bg-[color:var(--action-hover)]"
      >
        Reload page
      </button>
    </div>
  );
}
